import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '@core/services/api-base-url';
import {
  ClientCompanyRequest,
  ClientCompanyResponse,
  ClientConfigurationRequest,
  ClientConfigurationResponse,
  ClientRequest,
  ClientResponse,
  ClientTransactionHistoryResponse,
} from './client.model';

type ClientCompanyQuery = {
  clientId?: string | null;
  companyId?: string | null;
  includeInactive?: boolean | null;
};

type ClientConfigurationQuery = {
  clientId?: string | null;
  includeInactive?: boolean | null;  
};

type ClientQuery = {
  includeInactive?: boolean | null;
};

@Injectable({ providedIn: 'root' })
export class ClientApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = getApiBaseUrl();

  getClients(query: ClientQuery): Observable<ClientResponse[]> {
    return this.http.get<ClientResponse[]>(`${this.apiBaseUrl}clients`, {
      params: toQueryParams(query),
    });
  }

  getClient(clientId: string): Observable<ClientResponse> {
    return this.http.get<ClientResponse>(`${this.apiBaseUrl}clients/${clientId}`);
  }

  createClient(body: ClientRequest): Observable<ClientResponse> {
    return this.http.post<ClientResponse>(`${this.apiBaseUrl}clients`, body);
  }

  updateClient(clientId: string, body: ClientRequest): Observable<ClientResponse> {
    return this.http.put<ClientResponse>(`${this.apiBaseUrl}clients/${clientId}`, body);
  }

  deactivateClient(clientId: string): Observable<ClientResponse> {
    return this.http.put<ClientResponse>(`${this.apiBaseUrl}clients/${clientId}/deactivate`, {});
  }

  activateClient(clientId: string): Observable<ClientResponse> {
    return this.http.put<ClientResponse>(`${this.apiBaseUrl}clients/${clientId}/activate`, {});
  }

  getClientHistory(clientId: string): Observable<ClientTransactionHistoryResponse[]> {
    return this.http.get<ClientTransactionHistoryResponse[]>(`${this.apiBaseUrl}clients/${clientId}/history`);
  }

  getClientCompanies(query: ClientCompanyQuery): Observable<ClientCompanyResponse[]> {
    return this.http.get<ClientCompanyResponse[]>(`${this.apiBaseUrl}client-companies`, {
      params: toQueryParams(query),
    });
  }

  getClientCompany(clientCompanyId: string): Observable<ClientCompanyResponse> {
    return this.http.get<ClientCompanyResponse>(`${this.apiBaseUrl}client-companies/${clientCompanyId}`);
  }

  createClientCompany(body: ClientCompanyRequest): Observable<ClientCompanyResponse> {
    return this.http.post<ClientCompanyResponse>(`${this.apiBaseUrl}client-companies`, body);
  }

  updateClientCompany(clientCompanyId: string, body: ClientCompanyRequest): Observable<ClientCompanyResponse> {
    return this.http.put<ClientCompanyResponse>(`${this.apiBaseUrl}client-companies/${clientCompanyId}`, body);
  }

  deactivateClientCompany(clientCompanyId: string): Observable<ClientCompanyResponse> {
    return this.http.put<ClientCompanyResponse>(`${this.apiBaseUrl}client-companies/${clientCompanyId}/deactivate`, {});
  }

  activateClientCompany(clientCompanyId: string): Observable<ClientCompanyResponse> {
    return this.http.put<ClientCompanyResponse>(`${this.apiBaseUrl}client-companies/${clientCompanyId}/activate`, {});
  }

  getClientConfigurations(query: ClientConfigurationQuery): Observable<ClientConfigurationResponse[]> {
    return this.http.get<ClientConfigurationResponse[]>(`${this.apiBaseUrl}client-configurations`, {
      params: toQueryParams(query),
    });
  }

  getClientConfiguration(clientConfigurationId: string): Observable<ClientConfigurationResponse> {
    return this.http.get<ClientConfigurationResponse>(`${this.apiBaseUrl}client-configurations/${clientConfigurationId}`);
  }

  createClientConfiguration(body: ClientConfigurationRequest): Observable<ClientConfigurationResponse> {
    return this.http.post<ClientConfigurationResponse>(`${this.apiBaseUrl}client-configurations`, body);
  }

  updateClientConfiguration(clientConfigurationId: string, body: ClientConfigurationRequest): Observable<ClientConfigurationResponse> {
    return this.http.put<ClientConfigurationResponse>(`${this.apiBaseUrl}client-configurations/${clientConfigurationId}`, body);
  }

  deactivateClientConfiguration(clientConfigurationId: string): Observable<ClientConfigurationResponse> {
    return this.http.put<ClientConfigurationResponse>(`${this.apiBaseUrl}client-configurations/${clientConfigurationId}/deactivate`, {});
  }

  activateClientConfiguration(clientConfigurationId: string): Observable<ClientConfigurationResponse> {
    return this.http.put<ClientConfigurationResponse>(`${this.apiBaseUrl}client-configurations/${clientConfigurationId}/activate`, {});
  }
}

function toQueryParams(query: Record<string, string | boolean | null | undefined>): HttpParams {
  let params = new HttpParams();

  for (const [key, value] of Object.entries(query)) {
    if (value) {
      params = params.set(key, value.toString());
    }
  }

  return params;
}
